import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Paper, CircularProgress, Container,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Divider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../context/AuthContext';
import * as aiChat from '../utils/aiChat';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useNavigate } from 'react-router-dom';
import { getFallbackMessage } from '../utils/aiMatcher';

// Use environment variables or fallback to test numbers
const DEFAULT_PHONE = process.env.REACT_APP_DEFAULT_PHONE || '+15555555555';
const TWILIO_NUMBER = process.env.REACT_APP_TWILIO_NUMBER || '+19124173682';

const TestInterface = () => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Debug messages state changes
  useEffect(() => {
    console.log('Messages state updated:', messages);
  }, [messages]);

  // Fetch business data on component mount
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!currentUser) return;

      try {
        console.log('Fetching business data for user:', currentUser.uid);
        const businessDoc = await getDoc(doc(db, 'businesses', currentUser.uid));

        if (businessDoc.exists()) {
          const data = businessDoc.data();
          console.log('Business data found:', JSON.stringify(data, null, 2));

          // Log specific business info fields to help diagnose
          console.log('Business name:', data.businessInfo?.name);
          console.log('Business phone:', data.phoneData?.phoneNumber);
          console.log('Business hours:', data.businessInfo?.hours);

          // Ensure phoneData exists with correct structure
          if (!data.phoneData) {
            data.phoneData = {
              phoneNumber: data.businessProfile?.phoneNumber || data.businessInfo?.phoneNumber || data.phoneNumber || '',
              carrier: 'other',
              carrierDetails: {
                name: 'Other Carriers',
                activateCode: '**61*+19124173682#',
                deactivateCode: '##61#',
                instructions: 'Activate Missed Call Forwarding: Dial **61*+19124173682# (For most other carriers)'
              }
            };
            console.log('Created phoneData from existing phone number');
          }

          setBusinessData(data);
        } else {
          console.error('No business document found for user:', currentUser.uid);

          // Create a default business data object on error
          const defaultData = {
            businessInfo: {
              name: 'Your Business',
              description: 'Business description',
              hours: '9am-5pm Monday-Friday'
            },
            phoneData: {
              phoneNumber: '',
              carrier: 'other',
              carrierDetails: {
                name: 'Other Carriers',
                activateCode: '**61*+19124173682#',
                deactivateCode: '##61#',
                instructions: 'Activate Missed Call Forwarding: Dial **61*+19124173682# (For most other carriers)'
              }
            },
            updatedAt: new Date().toISOString()
          };

          console.log('Using default business data:', defaultData);
          setBusinessData(defaultData);
          setError('No business information found. Using default values.');
        }
      } catch (err) {
        console.error('Error fetching business data:', err);

        // Create a default business data object on error
        const defaultData = {
          businessInfo: {
            name: 'Your Business',
            description: 'Business description',
            hours: '9am-5pm Monday-Friday'
          },
          phoneData: {
            phoneNumber: '',
            carrier: 'other',
            carrierDetails: {
              name: 'Other Carriers',
              activateCode: '**61*+19124173682#',
              deactivateCode: '##61#',
              instructions: 'Activate Missed Call Forwarding: Dial **61*+19124173682# (For most other carriers)'
            }
          },
          updatedAt: new Date().toISOString()
        };

        console.log('Using default business data due to error:', defaultData);
        setBusinessData(defaultData);
        setError('Error fetching business data. Using default values.');
      }
    };

    fetchBusinessData();
  }, [currentUser]);

  // Scroll to bottom of message list
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !businessData || !conversationId) return;

    try {
      setLoading(true);
      console.log('Sending message:', message);

      // Add message to local state as 'pending'
      const userMessage = {
        content: message,
        sender: 'user',
        timestamp: new Date().toISOString(),
        status: 'pending'
      };

      setMessages(prevMessages => [...prevMessages, userMessage]);

      // Clear the input field
      setMessage('');

      const response = await aiChat.sendMessage(conversationId, message, currentUser.uid, businessData);
      console.log('Message sent, response:', response);

      // Update the user message status to sent
      setMessages(prevMessages => {
        return prevMessages.map((msg, index) => {
          if (index === prevMessages.length - 1 && msg.sender === 'user' && msg.status === 'pending') {
            return { ...msg, status: 'sent' };
          }
          return msg;
        });
      });

      // Handle both standard response format and fallback format
      if (response && response.data && response.data.reply) {
        // Standard response format
        const botReply = {
          content: response.data.reply,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          messageId: response.data.messageId || null
        };

        setMessages(prevMessages => [...prevMessages, botReply]);
      } else if (response && response.reply) {
        // Alternative response format
        const botReply = {
          content: response.reply,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          messageId: response.messageId || null
        };

        setMessages(prevMessages => [...prevMessages, botReply]);
      } else {
        console.warn('Unexpected response format:', response);
        // Fallback to a default message
        const fallbackReply = {
          content: getFallbackMessage(),
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          messageId: `fallback-${Date.now()}`
        };

        setMessages(prevMessages => [...prevMessages, fallbackReply]);

        setSnackbarMessage('Using fallback response due to unexpected format');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message: ' + (err.message || 'Unknown error'));

      // Add a fallback AI response
      const fallbackReply = {
        content: getFallbackMessage(),
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        messageId: `error-fallback-${Date.now()}`
      };

      setMessages(prevMessages => {
        // First, update the last user message status
        const updatedMessages = prevMessages.map((msg, index) => {
          if (index === prevMessages.length - 1 && msg.sender === 'user' && msg.status === 'pending') {
            return { ...msg, status: 'error' };
          }
          return msg;
        });

        // Then add the fallback message
        return [...updatedMessages, fallbackReply];
      });

      setSnackbarMessage('Failed to get a response. Using fallback message.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const createConversation = async () => {
    if (!businessData) {
      setError('No business data available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the business phone number from the business data or fallback to a default
      const businessPhone = businessData.phoneData?.phoneNumber || '+17073406661';
      console.log('Using business phone number for conversation:', businessPhone);
      console.log('Business data for conversation:', JSON.stringify(businessData, null, 2));

      // Create a conversation with detailed error handling
      try {
        const response = await aiChat.createConversation(currentUser.uid, businessPhone, businessData);
        console.log('Create conversation response:', response);

        if (response && response.conversationId) {
          console.log('Created conversation:', response);
          setConversationId(response.conversationId);

          // Welcome message - use the initialMessage from the response if available
          const welcomeMessage = response.initialMessage ||
                               businessData.introMessage ||
                               `Hello! I'm your AI assistant. How can I help you today?`;

          setMessages([
            {
              content: welcomeMessage,
              sender: 'assistant',
              timestamp: new Date().toISOString()
            }
          ]);

          setSnackbarMessage('Conversation created successfully');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } else {
          // This should not happen with the improved error handling in aiChat.js
          console.error('Failed to create conversation - invalid response format:', response);
          setError('Failed to create conversation - invalid response format');
          setSnackbarMessage('Could not create a new conversation - invalid response format');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);

          // Create a local conversation ID as fallback
          const fallbackId = `local-${Date.now()}`;
          console.log('Using fallback conversation ID:', fallbackId);
          setConversationId(fallbackId);

          // Set a default welcome message
          setMessages([
            {
              content: businessData.introMessage || `Hello! I'm your AI assistant. How can I help you today?`,
              sender: 'assistant',
              timestamp: new Date().toISOString()
            }
          ]);
        }
      } catch (apiError) {
        console.error('API error creating conversation:', apiError);

        // Create a local conversation as fallback
        const fallbackId = `local-${Date.now()}`;
        console.log('Using fallback conversation ID after API error:', fallbackId);
        setConversationId(fallbackId);

        // Set a default welcome message
        setMessages([
          {
            content: businessData.introMessage || `Hello! I'm your AI assistant. How can I help you today?`,
            sender: 'assistant',
            timestamp: new Date().toISOString()
          }
        ]);

        setError('API error creating conversation: ' + (apiError.message || 'Unknown error'));
        setSnackbarMessage('Using local conversation due to API error');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error in conversation creation process:', err);
      setError('Error in conversation creation process: ' + (err.message || 'Unknown error'));
      setSnackbarMessage('Failed to create conversation');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);

      // Create a local conversation as last resort fallback
      const emergencyId = `emergency-${Date.now()}`;
      console.log('Using emergency conversation ID:', emergencyId);
      setConversationId(emergencyId);

      // Set a default welcome message
      setMessages([
        {
          content: 'Hello! I\'m your AI assistant. How can I help you today?',
          sender: 'assistant',
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle simulating a missed call
  const handleSimulateMissedCall = async () => {
    // Logic to simulate a missed call
    console.log('Simulating missed call...');

    if (!businessData) {
      setError('No business data available');
      setSnackbarMessage('Business data is required to simulate a missed call');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the business phone number from the business data or fallback to a default
      const businessPhone = businessData.phoneData?.phoneNumber || '+17073406661';

      console.log('Using business phone number for conversation:', businessPhone);
      console.log('Business data for simulation:', JSON.stringify(businessData, null, 2));

      // Ensure we have the commonQuestions to test with
      if (!businessData.commonQuestions || businessData.commonQuestions.length === 0) {
        console.warn('No Q&A pairs found. Testing will default to fallback responses.');
        setSnackbarMessage('Warning: No Q&A pairs found. Add some in the dashboard to test AI responses.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      } else {
        console.log('Found Q&A pairs for testing:', businessData.commonQuestions);
      }

      // Create a conversation with detailed error handling
      try {
        const response = await aiChat.createConversation(currentUser.uid, businessPhone, businessData);
        console.log('Create conversation response:', response);

        if (response && response.conversationId) {
          console.log('Created conversation:', response);
          setConversationId(response.conversationId);

          // Reset messages array with initial AI message - use the initialMessage from the response if available
          const initialMessage = response.initialMessage ||
                               businessData.introMessage ||
                               "Sorry I missed your call! I'm currently with a customer. How can I help you today?";

          setMessages([
            {
              content: initialMessage,
              sender: 'assistant',
              timestamp: new Date().toISOString()
            }
          ]);

          // Show a helpful message about testing
          let helpMessage = 'Try asking questions related to:';
          if (businessData.commonQuestions && businessData.commonQuestions.length > 0) {
            businessData.commonQuestions.forEach((qa, index) => {
              if (index < 3) { // Limit to first 3 questions for brevity
                helpMessage += `\n- "${qa.question}"`;
              }
            });
            if (businessData.commonQuestions.length > 3) {
              helpMessage += `\n- ...and ${businessData.commonQuestions.length - 3} more questions`;
            }
          } else {
            helpMessage += ' business hours, location, or services. (Add Q&A pairs in dashboard for better responses)';
          }

          setSnackbarMessage(helpMessage);
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
        } else {
          // This should not happen with the improved error handling in aiChat.js
          console.error('Failed to create conversation - invalid response format:', response);
          setError('Failed to create conversation - invalid response format');
          setSnackbarMessage('Could not create a new conversation - invalid response format');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);

          // Create a local conversation ID as fallback
          const fallbackId = `local-${Date.now()}`;
          console.log('Using fallback conversation ID:', fallbackId);
          setConversationId(fallbackId);

          // Set a default welcome message
          const initialMessage = businessData.introMessage || "Sorry I missed your call! I'm currently with a customer. How can I help you today?";
          setMessages([
            {
              content: initialMessage,
              sender: 'assistant',
              timestamp: new Date().toISOString()
            }
          ]);
        }
      } catch (apiError) {
        console.error('API error creating conversation:', apiError);

        // Create a local conversation as fallback
        const fallbackId = `local-${Date.now()}`;
        console.log('Using fallback conversation ID after API error:', fallbackId);
        setConversationId(fallbackId);

        // Set a default welcome message
        const initialMessage = businessData.introMessage || "Sorry I missed your call! I'm currently with a customer. How can I help you today?";
        setMessages([
          {
            content: initialMessage,
            sender: 'assistant',
            timestamp: new Date().toISOString()
          }
        ]);

        setError('API error creating conversation: ' + (apiError.message || 'Unknown error'));
        setSnackbarMessage('Using local conversation due to API error');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error in conversation creation process:', err);
      setError('Error in conversation creation process: ' + (err.message || 'Unknown error'));
      setSnackbarMessage('Failed to create conversation');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);

      // Create a local conversation as last resort fallback
      const emergencyId = `emergency-${Date.now()}`;
      console.log('Using emergency conversation ID:', emergencyId);
      setConversationId(emergencyId);

      // Set a default welcome message
      setMessages([
        {
          content: "Sorry I missed your call! I'm currently with a customer. How can I help you today?",
          sender: 'assistant',
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
      // Don't open dialog here to avoid confusion
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            AI Test Interface
          </Typography>

          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            variant="outlined"
            sx={{ mr: 2, mb: 2 }}
          >
            Back to Home
          </Button>

          {businessData && (
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Business Information
              </Typography>
              <Typography variant="subtitle1" component="div">
                Name: {businessData?.businessInfo?.name || 'Not available'}
              </Typography>
              <Typography variant="subtitle1" component="div">
                Phone: {businessData?.phoneData?.phoneNumber || 'Loading...'}
              </Typography>
              {businessData?.businessInfo?.hours && (
                <Typography variant="subtitle1" component="div">
                  Hours: {businessData.businessInfo.hours}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSimulateMissedCall}
                startIcon={<span role="img" aria-label="phone">📞</span>}
                sx={{ mt: 2 }}
              >
                Simulate Missed Call
              </Button>
            </Paper>
          )}

          {!conversationId ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={loading || !businessData}
                onClick={createConversation}
                startIcon={<AddIcon />}
              >
                Start New Conversation
              </Button>
            </Box>
          ) : (
            <Box>
              <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle1">
                  Missed call from customer to {businessData?.phoneData?.phoneNumber || DEFAULT_PHONE}
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ p: 2, mb: 3, maxHeight: '400px', overflow: 'auto' }}>
                {messages.length === 0 ? (
                  <Typography align="center" color="textSecondary">
                    No messages yet
                  </Typography>
                ) : (
                  <List>
                    {messages.map((msg, index) => (
                      <ListItem
                        key={index}
                        alignItems="flex-start"
                        sx={{
                          flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
                          mb: 1
                        }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            maxWidth: '80%',
                            bgcolor: msg.sender === 'user' ? '#e3f2fd' : '#f1f8e9',
                            borderRadius: '8px'
                          }}
                        >
                          <Typography variant="body1">{msg.content}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                            {new Date(msg.timestamp).toLocaleTimeString()}
                            {msg.status === 'pending' && ' (sending...)'}
                          </Typography>
                        </Paper>
                      </ListItem>
                    ))}
                    <div ref={messagesEndRef} />
                  </List>
                )}
              </Paper>

              <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  fullWidth
                  label="Type your message"
                  variant="outlined"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={loading}
                  sx={{ mr: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !message.trim()}
                  endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  Send
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {error && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#ffebee' }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {conversationId && (
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6">Debug Information</Typography>
            <Typography variant="body2">Conversation ID: {conversationId}</Typography>
            <Typography variant="body2">Message Count: {messages.length}</Typography>
            <Typography variant="body2">Loading: {loading ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2">Business Name: {businessData?.businessInfo?.name || 'Not loaded'}</Typography>
            <Typography variant="body2">Business Phone: {businessData?.phoneData?.phoneNumber || 'Not loaded'}</Typography>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Missed Call Simulation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            This would simulate a customer calling your business number and not getting an answer.
          </Typography>
          <Typography variant="body1" paragraph>
            In a real scenario, this would:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="1. Create a new conversation in Firestore" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Send an initial message to the customer" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Notify you about the missed call" />
            </ListItem>
          </List>
          <Typography variant="body1" color="primary">
            For now, you can just start a new conversation manually with the button above.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestInterface;
